import {
  assertIsValidPath,
  isPath,
  type Path,
  type PathAssertion,
  removeTrailingSlashesFromPath,
} from '@/src/lib/urls';

import { InvalidPathPatternError } from './errors';

const findDuplicates = (values: string[]): string[] => {
  const seen = new Set<string>();
  const duplicates = new Set<string>();
  for (const value of values) {
    if (seen.has(value)) {
      duplicates.add(value);
    } else {
      seen.add(value);
    }
  }
  return [...duplicates];
};

const humanizePlaceholderList = (values: string[]): string => {
  const formatted = values.map(v => `:${v}`);
  if (formatted.length === 0) {
    return '';
  }
  if (formatted.length === 1) {
    return formatted[0];
  }
  const head = formatted.slice(0, -1).join(', ');
  const tail = formatted[formatted.length - 1];
  const oxford = formatted.length >= 3 ? ',' : '';
  return `${head}${oxford} and ${tail}`;
};

/**
 * A regular expression that is used to match placeholder segments (i.e. :someId) in a path pattern.
 */
const PathPlaceholderRegex = /:([A-Za-z0-9-]+?)(?:$|\/)/g;

/**
 * A regular expression that matches a single placeholder segment in a path pattern, with no
 * leading slash and no trailing boundary. Captures the placeholder name in group 1.
 *
 * Useful when consumers need to classify a single, already-split path segment without invoking
 * the global, boundary-consuming {@link PathPlaceholderRegex}.
 *
 * @example
 * PathPlaceholderSegmentRegex.exec(':slug');?.[1]; // 'slug'
 * PathPlaceholderSegmentRegex.exec(':slug');       // [':slug', 'slug']
 * PathPlaceholderSegmentRegex.exec('cohorts');     // null
 */
export const PathPlaceholderSegmentRegex = /^:([A-Za-z0-9-]+)$/;

/**
 * A regular expression that is used to match valid "path patterns".
 *
 * A "path pattern" is a "path" that is allowed to contain placeholder segments, :id, that represent
 * locations in the path where dynamic path parameters should be injected.
 */
const PathPatternRegex = /^\/(?:(?:[:a-zA-Z0-9-]+\/)*[:a-zA-Z0-9-]+)?\/?$/;

/**
 * The character class a placeholder segment's concrete value may take. Broader than a strict UUID:
 * report slugs (e.g. `usersNeverSignedIn` or underscore-separated inventory ids) also flow through
 * placeholder segments, so the class must stay in sync with the slug vocabulary those pages
 * validate against (`[a-zA-Z0-9_-]`).
 */
const UuidPathParamRegex = '([0-9a-zA-Z_-]+)';
const ExactPathEndRegex = '(?:\\/)?(\\?([^\\/]+)?(\\/)?)?$';

const createUuidPathParamRegex = (exclude?: string[]) => {
  if (exclude?.length) {
    return `(?!${exclude.join('|')})${UuidPathParamRegex}`;
  }
  return UuidPathParamRegex;
};

/**
 * A type assertion that asserts that the provided value, {@link string}, is consistent with the
 * {@link Path} type definition, **and** that the form of the provided value is a valid path
 * pattern, based on the {@link PathPatternRegex}.
 *
 * Additionally, the type assertion ensures that the provided pattern does not contain duplicate
 * placeholder segments (i.e, "/resources/:id/sub-resource/:id").
 *
 * @param {string} path
 *   The value that should be asserted to be a valid path, {@link Path}, and consistent with the
 *   form of a valid path pattern, as determined by the {@link PathPatternRegex}.
 *
 * @throws {InvalidPathError}
 *   Throws an error, {@link InvalidPathPatternError}, if the provided value, {@link string}, is not
 *   consistent with the type definition of a {@link Path}, the form of the path pattern is invalid,
 *   as deemed by the {@link PathPatternRegex}, or the path contains duplicate placeholder segments.
 */
export const assertIsValidPathPattern: PathAssertion = path => {
  if (!isPath(path)) {
    throw new InvalidPathPatternError(path, "The path does not begin with a '/'.");
  }
  if (!PathPatternRegex.test(path)) {
    throw new InvalidPathPatternError(path, 'The path does not match the expected regex.');
  }
  const placeholders = _parsePathPlaceholders(path);
  const duplicatePlaceholders = findDuplicates(placeholders);
  if (duplicatePlaceholders.length !== 0) {
    throw new InvalidPathPatternError(
      path,
      'The path contains duplicate placeholder segment(s): ' +
        humanizePlaceholderList(duplicatePlaceholders) +
        '.',
    );
  }
};

export interface CreatePathRegexOptions {
  /**
   * Whether or not the {@link RegExp} for the path should match the exact path, or whether it
   * should match paths that extend off of the provided path.
   *
   * For instance, if 'exact' is set to 'true', the {@link RegExp} created for the path
   * "/organizationPrograms/:id/levels/:id" will match "/organizationPrograms/5/levels/5" but will
   * not match "/organizationPrograms/5/levels/5/activities/5".  If 'exact' is set to 'false', on
   * the other hand, the {@link RegExp} will match both paths.
   *
   * When 'exact' is 'true', the {@link RegExp} will still match the exact path when query
   * parameters are present at the end of the path - so long as there are no additional path
   * segments.
   *
   * @default false
   */
  readonly exact?: boolean;
  /**
   * An array of path parameter values that should not be treated as ID placeholder segments in the
   * constructed {@link RegExp}.
   *
   * For instance, if this option is defined as ['archived', 'favorited'], and the provided path
   * is '/cohorts/:id', the resulting {@link Regex} will match '/cohorts/1', but will not match
   * '/cohorts/archived' or '/cohorts/favorited'.
   */
  readonly excludePathParams?: string[];
}

/**
 * Creates a regular expression, {@link RegExp}, that can be used to match path patterns - which
 * can contain placeholder segments that represent dynamic UUID path parameters that should be
 * injected before the full path, {@link Path}, is constructed.
 *
 * @param {string} pattern
 *   The {@link Path} pattern that represents the path with potential placeholder segments for which
 *   the {@link RegExp} should be created.
 * @param {CreatePathRegexOptions} options
 *   Options that define how the {@link RegExp} is constructed.
 *
 *   @see {CreatePathRegexOptions}
 *
 * @example
 * // This regex will match all of the following:
 * // 1. "/customers/407e42c3-4b3d-4f53-9263-4d5d3a4cd35a"
 * // 2. "/customers/407e42c3-4b3d-4f53-9263-4d5d3a4cd35a/"
 * // 3. "/customers/407e42c3-4b3d-4f53-9263-4d5d3a4cd35a?name=X"
 * // 4. "/customers/407e42c3-4b3d-4f53-9263-4d5d3a4cd35a?name=X/"
 * const regex = createPathRegex("/customers/:customerId/", { exact: true });
 */
export const createPathRegex = (pattern: Path, options?: CreatePathRegexOptions): RegExp => {
  const exact = options?.exact ?? false;

  let regex = removeTrailingSlashesFromPath(pattern);

  const placeholders = parsePathPlaceholders(pattern);
  for (const placeholder of placeholders) {
    regex = regex.replace(
      `:${placeholder}`,
      createUuidPathParamRegex(options?.excludePathParams),
    ) as Path;
  }
  if (exact) {
    return new RegExp(`^${regex}${ExactPathEndRegex}`);
  }
  return new RegExp(`^${regex}`);
};

/**
 * A generic type that infers the names of the path pattern placeholders for a given path pattern,
 * {@link Path}.
 *
 * @example
 * // "programId" | "activityId"
 * type Params = PathPlaceholder<"/organizationPrograms/:programId/activities/:activityId">;
 */
export type PathPlaceholder<
  P extends Path,
  _Params extends string = never,
> = P extends `/${infer L}/:${infer Param}/${infer R}${'' | '/'}`
  ? PathPlaceholder<`/${L}/${R}`, _Params | Param>
  : P extends `/${infer L}/:${infer Param}${'' | '/'}`
    ? PathPlaceholder<`/${L}`, _Params | Param>
    : P extends `/:${infer Param}/${infer R}`
      ? PathPlaceholder<`/${R}`, _Params | Param>
      : [_Params] extends [never]
        ? string
        : _Params;

const _parsePathPlaceholders = (pattern: Path): string[] => {
  const matches = pattern.matchAll(PathPlaceholderRegex);
  const params: string[] = [];
  for (const match of matches) {
    params.push(match[1]);
  }
  return params;
};

/**
 * Parses and returns the placeholder path parameters from the provided path pattern, {@link Path}.
 *
 * @param {Path} pattern
 *   The path pattern that may contain placeholder path parameters that should be returned.
 *
 * @example
 * // ["programId", "activityId"]
 * parsePathPlaceholders("/organizationPrograms/:programId/activities/:activityId");
 */
export const parsePathPlaceholders = <P extends Path>(pattern: P): PathPlaceholder<P>[] => {
  assertIsValidPathPattern(pattern);
  return _parsePathPlaceholders(pattern) as PathPlaceholder<P>[];
};

/**
 * A generic type that represents a record-type of the path parameter placeholders,
 * {@link PathPlaceholder}, that are necessary to construct a concrete path, {@link Path},
 * from a given path pattern, {@link Path}.
 *
 * @example
 * // { programId: string; activityId: string; }
 * type Params = PathParameters<"/organizationPrograms/:programId/activities/:activityId">;
 */
export type PathParameters<P extends Path> = Record<PathPlaceholder<P>, string>;

/**
 * A generic type that represents the form of a path pattern, {@link Path}, with it's path parameter
 * placeholders, {@link PathPlaceholder}, replaced with generic {@link string} types.
 *
 * @example
 * // `/organizationPrograms/${string}/activities/${string}`
 * type Params = GeneralizedPath<"/organizationPrograms/:programId/activities/:activityId">;
 */
export type GeneralizedPath<P extends string> = P extends `${infer L}/:${infer _}/${infer R}`
  ? GeneralizedPath<`${L}/${string}/${R}`>
  : P extends `${infer L}/:${infer _}${'' | '/'}`
    ? GeneralizedPath<`${L}/${string}`>
    : P extends `:${infer _}/${infer R}`
      ? GeneralizedPath<`${string}/${R}`>
      : P;

/**
 * A generic type that represents a path pattern, {@link P}, with its path parameter placeholders,
 * {@link PathPlaceholder}, replaced with concrete {@link string} types that exist in the
 * set of path parameters, {@link PathParameters}, represented by the generic type
 * {@link Params}.
 *
 * @example
 * // `/organizationPrograms/1/activities/5`
 * type Concrete = ConcretePath<
 *   "/organizationPrograms/:programId/activities/:activityId",
 *   { programId: '1'; activityId: '5'; }
 * >;
 */
export type ConcretePath<
  P extends string,
  Params extends Record<string, string>,
> = P extends `${infer L}/:${infer K extends Extract<keyof Params, string>}/${infer R}`
  ? ConcretePath<`${L}/${Params[K]}/${R}`, Params>
  : P extends `${infer L}/:${infer K extends Extract<keyof Params, string>}${'' | '/'}`
    ? ConcretePath<`${L}/${Params[K]}`, Params>
    : P extends `:${infer K extends Extract<keyof Params, string>}/${infer R}`
      ? ConcretePath<`${Params[K]}/${R}`, Params>
      : P;

/**
 * Injects the provided path parameters, {@link Params}, into the provided path, {@link P}, where
 * the path {@link P} contains placeholder segments that are prefixed with a colon (e.g. `:id`).
 *
 * @param {Path} pattern
 *   The path pattern that contains placeholder segments that should be replaced with the provided
 *   parameter values in {@link Params}.
 * @param {Params} params
 *   The parameters that should be injected into the provided path pattern at the locations where
 *   the placeholders exist.
 *
 * @example
 * // Returns `/organizationPrograms/5/activities/1`
 * constructConcretePath("/organizationPrograms/:programId/activities/:activityId", {
 *    activityId: "1",
 *    programId: "5",
 * })
 *
 * @returns {ConcretePath<P, Params>}
 *   The path with all placeholders replaced with the provided parameter values.
 */
export const constructConcretePath = <P extends Path, Params extends PathParameters<P>>(
  pattern: P,
  params: Params,
): ConcretePath<P, Params> & string => {
  let path: string = pattern;

  const placeholders = parsePathPlaceholders(pattern);
  for (const placeholder of placeholders) {
    const value = params[placeholder];
    if (!value || value.trim().length === 0) {
      throw new Error(
        `The value '${value}' provided for the placeholder '${placeholder}' is not valid!`,
      );
    }
    path = path.replace(`:${placeholder}`, value.trim());
  }
  // This assertion will ensure that there are no leftover placeholder parameters in the path.
  assertIsValidPath(path);
  return path as ConcretePath<P, Params>;
};
